import type { FirmBranch } from '@/types/models'

/**
 * Grubun bir alandaki firma/şube seçimi (backend `groups.service.firm_scope`): `firms` tüm şubeleriyle açık
 * firmalar, `branches` yalnız seçili şubeleri açık firmalar. İkisi de boşsa kısıt yoktur.
 */
export interface FirmBranchSelection {
  firms: string[]
  branches: FirmBranch[]
}

export const EMPTY_SELECTION: FirmBranchSelection = { firms: [], branches: [] }

export function isUnrestricted(selection: FirmBranchSelection): boolean {
  return selection.firms.length === 0 && selection.branches.length === 0
}

/** Firmanın tümünü açar ya da kapatır; açılınca o firmanın tek tek şube seçimi düşer. */
export function toggleFirm(selection: FirmBranchSelection, firma: string): FirmBranchSelection {
  if (selection.firms.includes(firma)) {
    return { ...selection, firms: selection.firms.filter((item) => item !== firma) }
  }
  return {
    firms: [...selection.firms, firma],
    branches: selection.branches.filter((branch) => branch.firma !== firma),
  }
}

export function hasBranch(selection: FirmBranchSelection, firma: string, sube: string): boolean {
  return selection.branches.some((branch) => branch.firma === firma && branch.sube === sube)
}

/** Tek şubeyi açar ya da kapatır (firmanın tümü açıksa şube seçimi anlamsız, değişmez). */
export function toggleBranch(
  selection: FirmBranchSelection,
  firma: string,
  sube: string,
): FirmBranchSelection {
  if (selection.firms.includes(firma)) return selection
  return {
    ...selection,
    branches: hasBranch(selection, firma, sube)
      ? selection.branches.filter((branch) => !(branch.firma === firma && branch.sube === sube))
      : [...selection.branches, { firma, sube }],
  }
}

/** Firmanın kutusu: tümü açık = işaretli, bazı şubeleri açık = belirsiz. */
export function firmState(selection: FirmBranchSelection, firma: string): 'all' | 'some' | 'none' {
  if (selection.firms.includes(firma)) return 'all'
  return selection.branches.some((branch) => branch.firma === firma) ? 'some' : 'none'
}

/** Okunur özet: tüm firma adı ya da "Firma / Şube". */
export function selectionLabels(selection: FirmBranchSelection): string[] {
  return [
    ...selection.firms,
    ...selection.branches.map((branch) => `${branch.firma} / ${branch.sube}`),
  ]
}
